// IndexedDB helper for offline storage
const DB_NAME = 'DumontSalesDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingNota';

let db = null;

// Initialize IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject('IndexedDB error: ' + request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { 
          keyPath: 'id',
          autoIncrement: true 
        });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('synced', 'synced', { unique: false });
      }
    };
  });
};

// Save nota to IndexedDB
export const saveNotaOffline = async (notaData) => {
  try {
    await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const dataToSave = {
      ...notaData,
      timestamp: new Date().toISOString(),
      synced: false
    };

    return new Promise((resolve, reject) => {
      const request = store.add(dataToSave);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving to IndexedDB:', error);
    throw error;
  }
};

// Get all pending (unsynced) nota
export const getPendingNota = async () => {
  try {
    await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const allNota = request.result || [];
        const pending = allNota.filter(nota => !nota.synced);
        resolve(pending);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting pending nota:', error);
    return [];
  }
};

// Mark nota as synced
export const markNotaSynced = async (id) => {
  try {
    await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (data) {
          data.synced = true;
          const updateRequest = store.put(data);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve();
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('Error marking nota as synced:', error);
    throw error;
  }
};

// Delete synced nota from IndexedDB
export const deleteSyncedNota = async (id) => {
  try {
    await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error deleting synced nota:', error);
    throw error;
  }
};

// Get count of pending nota
export const getPendingCount = async () => {
  try {
    const pending = await getPendingNota();
    return pending.length;
  } catch (error) {
    console.error('Error getting pending count:', error);
    return 0;
  }
};